import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const STREAMS = [
  ["photography", "Photography", "#1A1A1A"],
  ["workshops", "Workshops", "#E4572E"],
  ["gift_cards", "Gift Cards", "#C9A24B"],
];

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default function AnalyticsTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!data) return <p className="text-sm text-[#7A7A7A] py-10 text-center">Loading…</p>;

  const maxTotal = Math.max(...data.months.map((m) => m.total), 1);

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      {/* headline stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Sales this month", money(data.revenue_this_month), data.month_label, "stat-revenue"],
          ["Still to collect", money(data.outstanding_balance), "unpaid session balances", "stat-outstanding"],
          ["Sessions this month", data.sessions_this_month, "on the calendar", "stat-sessions"],
          ["New inquiries", data.inquiries_new, "waiting for a reply", "stat-inquiries"],
        ].map(([label, value, sub, tid]) => (
          <div key={tid} className="bg-white border border-[#E5E5E2] p-6" data-testid={tid}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">{label}</p>
            <p className="font-serif text-3xl font-light">{value}</p>
            <p className="text-xs text-[#B5B5B0] mt-1">{sub}</p>
          </div>
        ))}
      </div>

      {/* streams this month */}
      <div className="bg-white border border-[#E5E5E2] p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Where it came from — {data.month_label}</p>
        <div className="grid sm:grid-cols-4 gap-4">
          {STREAMS.map(([key, label, color]) => (
            <div key={key} className="flex items-center gap-3" data-testid={`stream-${key}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
              <div>
                <p className="text-sm font-medium">{money(data.streams_this_month[key])}</p>
                <p className="text-xs text-[#7A7A7A]">{label}</p>
              </div>
            </div>
          ))}
        </div>
        {data.gift_cards_active_value > 0 && (
          <p className="text-xs text-[#7A7A7A] mt-4 border-t border-[#EFEFEC] pt-3">
            Active gift cards out there: <span className="text-[#1A1A1A] font-medium">{money(data.gift_cards_active_value)}</span> not yet redeemed
          </p>
        )}
      </div>

      {/* 6-month chart */}
      <div className="bg-white border border-[#E5E5E2] p-6" data-testid="analytics-chart">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-6">Last 6 months</p>
        <div className="flex items-end gap-3 sm:gap-6 h-48">
          {data.months.map((m) => (
            <div key={m.key} className="flex-1 flex flex-col items-center gap-2 h-full justify-end" data-testid={`chart-month-${m.key}`}>
              <p className="text-xs text-[#4A4A4A] font-medium">{m.total > 0 ? money(m.total) : ""}</p>
              <div className="w-full max-w-[56px] flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ height: `${Math.max((m.total / maxTotal) * 100, 2)}%` }}>
                {STREAMS.map(([key, , color]) => (
                  m[key] > 0 && <div key={key} style={{ backgroundColor: color, height: `${(m[key] / (m.total || 1)) * 100}%` }} />
                ))}
              </div>
              <p className="text-[10px] uppercase tracking-[0.1em] text-[#7A7A7A]">{m.label}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-[#EFEFEC]">
          {STREAMS.map(([key, label, color]) => (
            <span key={key} className="inline-flex items-center gap-2 text-[11px] text-[#4A4A4A]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} /> {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
